/**
 * ═══════════════════════════════════════════════════════════════════════
 * Unified Auth API Service
 * ═══════════════════════════════════════════════════════════════════════
 *
 * Single source of truth for all auth API calls.
 * Every request includes the required `userType` field.
 *
 * All auth calls now go through the FastAPI backend (/api/auth/*) which
 * proxies to the API Gateway Lambdas.  This ensures httpOnly cookies are
 * set and read on the SAME origin (same-site), fixing the cross-site
 * cookie problem that prevented token refresh from working.
 *
 * Routes (via FastAPI proxy):
 *   POST /api/auth/signup           → signup()
 *   POST /api/auth/login            → login()
 *   POST /api/auth/verify-otp       → verifyOtp()
 *   POST /api/auth/set-password     → setPassword()
 *   POST /api/auth/forgot-password  → forgotPassword()
 *   POST /api/auth/reset-password   → resetPassword()
 *   POST /api/auth/refresh          → refreshSession()
 */


import { signOut as amplifySignOut, getCurrentUser } from '@aws-amplify/auth';
import { API_CONFIG, STORAGE_KEYS } from '../../constants';
import { clearAuthToken } from '../api/apiClient';
import { clearStoredSession } from '../api/sessionApi';

// The org-scoped slice of `procedureStore`, back to its initial values.
// `pickerDeptId` and `_pickerInFlight` MUST go with `pickerProcedures`:
// `fetchProceduresForDept` short-circuits when `pickerDeptId` already matches
// the requested dept, so blanking the list alone would leave the next org with
// a permanently EMPTY lab picker instead of its own catalogue. `procedures` is
// the admin catalogue — same org-scoped data, same sign-out lifetime.
const PROCEDURE_STORE_SIGNED_OUT = {
  procedures: [],
  pickerProcedures: [],
  pickerDeptId: undefined,
  _pickerInFlight: null,
};

// ── Helpers ──────────────────────────────────────────────────────────

/**
 * POST to an auth endpoint (via FastAPI proxy) and return parsed JSON.
 * Throws a structured error on failure.
 */
async function authFetch(path, body) {
  const url = `${API_CONFIG.BACKEND_URL}${path}`;

  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });

  const text = await res.text();
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { error: `Invalid response from server` };
  }

  if (!res.ok || data.success === false || data.error) {
    const err = new Error(data.error || `Request failed (${res.status})`);
    err.code = data.code || null;
    err.statusCode = res.status;
    err.details = data.details || null;
    throw err;
  }

  return data.data || data;
}

// ═══════════════════════════════════════════════════════════════════════
//  Public API — matches the exact backend contract
// ═══════════════════════════════════════════════════════════════════════

/**
 * POST /auth/signup
 *
 * @param {{ userType, loginMethod, email?, phone? }} params
 * @returns {Promise<{ message, session, username }>}
 */
export async function signup({ userType, loginMethod, email, phone }) {
  const payload = { userType, loginMethod };
  if (email) payload.email = email;
  if (phone) payload.phone = phone;
  return authFetch('/api/auth/signup', payload);
}

/**
 * POST /auth/login  (password-based)
 *
 * @param {{ identifier: string, password: string, rememberMe?: boolean }} params
 * @returns {Promise<{ userId, username, agent, groups, expiresAt, idToken, accessToken, message }>}
 */
export async function login({ identifier, password, rememberMe = false }) {
  return authFetch('/api/auth/login', { identifier, password, rememberMe });
}

/**
 * POST /auth/verify-otp
 *
 * @param {{ userType, username, otp, session }} params
 * @returns {Promise<{ userId, agent, groups, expiresAt, accessToken, idToken, message }>}
 */
export async function verifyOtp({ userType, username, otp, session }) {
  return authFetch('/api/auth/verify-otp', { userType, username, otp, session });
}

/**
 * POST /auth/set-password
 *
 * `session` = the NEW_PASSWORD_REQUIRED challenge session returned by
 * login (requiresPasswordChange) or verify-otp (requiresPassword). Required.
 *
 * @param {{ userType, username, password, session }} params
 * @returns {Promise<{ message, accessToken?, idToken? }>}
 */
export async function setPassword({ userType, username, password, session }) {
  return authFetch('/api/auth/set-password', { userType, username, password, session });
}

/**
 * POST /auth/forgot-password
 *
 * @param {{ userType, loginMethod, email?, phone? }} params
 * @returns {Promise<{ message, channel, expiresIn }>}
 */
export async function forgotPassword({ userType, loginMethod, email, phone }) {
  const payload = { userType, loginMethod };
  if (email) payload.email = email;
  if (phone) payload.phone = phone;
  return authFetch('/api/auth/forgot-password', payload);
}

/**
 * POST /auth/reset-password
 *
 * @param {{ userType, username, otp, newPassword }} params
 * @returns {Promise<{ message }>}
 */
export async function resetPassword({ userType, username, otp, newPassword }) {
  return authFetch('/api/auth/reset-password', { userType, username, otp, newPassword });
}

// ═══════════════════════════════════════════════════════════════════════
//  Session utilities (migrated from legacy authService.js)
// ═══════════════════════════════════════════════════════════════════════

/**
 * POST /auth/refresh
 *
 * Uses the httpOnly refreshToken cookie (sent automatically by the browser)
 * to obtain fresh idToken and accessToken from Cognito.
 *
 * On success, updates the in-memory JWT token used by apiClient.
 *
 * @returns {Promise<{ idToken, accessToken, expiresAt, message }>}
 * @throws Error if the refresh token is expired/invalid (user must re-login)
 */
export async function refreshSession() {
  // Pass the (possibly expired) idToken in the body so the Lambda can
  // decode the cognitoUsername (UUID) for SECRET_HASH computation.
  // The idToken cookie may have been deleted by the browser (Max-Age = JWT
  // lifetime), but the JWT is still available in-memory / localStorage.
  let idTokenForHash = null;
  try {
    const { getAuthToken } = await import('../api/apiClient');
    idTokenForHash = getAuthToken();
  } catch { /* best-effort */ }

  const data = await authFetch('/api/auth/refresh', {
    ...(idTokenForHash ? { idToken: idTokenForHash } : {}),
  });

  // Update the in-memory + sessionStorage token so apiClient uses the fresh one
  if (data?.idToken) {
    const { setAuthToken } = await import('../api/apiClient');
    setAuthToken(data.idToken);
  }

  // Update stored expiry
  if (data?.expiresAt) {
    const { STORAGE_KEYS } = await import('../../constants');
    localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRY, String(data.expiresAt));
  }

  return data;
}

/**
 * Sign out the current user. Idempotent + dedup-safe.
 *
 * Multiple concurrent callers (user clicks button, F1 guard fires from
 * sessionApi, a cross-tab broadcast handler, etc.) all receive the SAME
 * in-flight promise rather than each kicking off a separate
 * `fetch('/api/auth/logout')` call. This:
 *   - Stops the "two Lambda invocations from one impatient double-click"
 *     pattern (the second click would hit empty cookies and waste a Lambda
 *     invoke).
 *   - Stops duplicate parallel RevokeToken calls when F1 + a button click
 *     race.
 *   - Cheap (single boolean + single promise variable).
 *
 * Sequence (per Part 2 design):
 *   1. POST /api/auth/logout — server revokes the refresh token at
 *      Cognito (RevokeToken) and returns Set-Cookie: Max-Age=0 headers
 *      that delete the browser's httpOnly cookies.  Best-effort — a
 *      network failure or backend hiccup must not trap the user in a
 *      "can't sign out" state.
 *   2. clearAuthToken() — wipes in-memory + localStorage idToken,
 *      cancels the scheduled refresh, and broadcasts 'logout' to other tabs.
 *   3. clearStoredSession() — removes the session metadata keys
 *      route guards check (agentType, tokenExpiry, userId, ...).
 *   4. Remove REMEMBER_ME flag.
 *   5. amplifySignOut() — local Amplify clear (no-op for our flow but
 *      kept for any Amplify-managed state).
 */
let _inflightSignOut = null;

/**
 * Scan & Share caches (org-scoped): counter QR codes carry the org's HIP ID, the waiting list holds its
 * patients, the cached org profile feeds the printed counter card. ONE helper for both sign-out paths
 * so they cannot drift; the stores' reset() also drops any request still in flight.
 */
const resetScanShareCaches = () => Promise.all([
  import('../../stores/scanCountersStore').then((m) => m.default.getState().reset()),
  import('../../stores/scanQueueStore').then((m) => m.default.getState().reset()),
  import('../../stores/abdmModeStore').then((m) => m.default.getState().reset()),
  import('../api/organisationProfileApi').then((m) => m.resetOrganisationProfile()),
]).catch(() => { /* non-critical */ });

/**
 * Optimistic sign-out — for UI callers that want instant navigation.
 *
 * Synchronously clears local auth state (in-memory token, session metadata,
 * REMEMBER_ME flag, Amplify local store) so the caller can navigate to /login
 * immediately. The server-side revocation (POST /api/auth/logout) is fired in
 * the background and the returned promise is for OPTIONAL observation only —
 * caller should NOT await it before navigating.
 *
 * A network failure on the background request is harmless: the auth cookies
 * expire on the browser side via Max-Age, and the refresh token at Cognito
 * expires naturally (30d) — same fallback as today's best-effort signOut().
 *
 * Respects the _inflightSignOut dedupe so concurrent calls don't double-fire
 * the network request.
 *
 * @returns {Promise<void>} background revoke promise — safe to ignore or
 *                          .catch() for telemetry.
 */
export const signOutOptimistic = () => {
  // Local clears — synchronous, do not throw.
  try { clearAuthToken(); } catch { /* non-critical */ }
  try { clearStoredSession(); } catch { /* non-critical */ }
  try { localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME); } catch { /* non-critical */ }
  try { amplifySignOut(); } catch { /* non-critical */ }
  // Reset the resolved chart-type cache so the next account re-resolves
  // its own specialty (otherwise a dentist's cached 'dental' leaks the
  // tooth chart to the next, non-dentist login until a full reload).
  // Dynamic import keeps the heavy chart module out of the auth bundle;
  // fire-and-forget is safe — the reset lands long before the next
  // login's tour-status fetch resolves.
  import('../../charts/core/useChartType')
    .then((m) => m.resetChartTypeCache())
    .catch(() => { /* non-critical */ });

  // Reset the other session-scoped client caches for the same reason —
  // don't let one account's staff roster / tour status / workflow patterns
  // leak into the next login.
  import('../api/staffApi').then((m) => m.resetStaffRoster()).catch(() => { /* non-critical */ });
  import('../api/prescriptionTourApi').then((m) => m.resetTourStatusCache()).catch(() => { /* non-critical */ });
  // Admission desk search caches hold admission rows (dept-walled per user).
  import('../../components/admission/useAdmissionSearchIndex')
    .then((m) => m.clearAdmissionSearchIndex())
    .catch(() => { /* non-critical */ });
  import('../../stores/workflowStore')
    .then((m) => m.default.setState({ patterns: null, patternsFetched: false }))
    .catch(() => { /* non-critical */ });
  // Same reason, for the two caches the prescription screen reads: the org's
  // LAB CATALOGUE (rendered in the Rx dropdown and the Tests picker) and the
  // Rx reference blob (the doctor's own top medicines/complaints/diagnoses,
  // rendered as one-tap quick-add pills). Both are org-scoped and both survive
  // a sign-out, which is a router transition and not a reload.
  import('../../stores/procedureStore')
    .then((m) => m.default.setState(PROCEDURE_STORE_SIGNED_OUT))
    .catch(() => { /* non-critical */ });
  resetScanShareCaches();
  import('../../components/visit/DigitalPrescriptionForm')
    .then((m) => m.resetRxReferenceCache())
    .catch(() => { /* non-critical */ });

  // If a network revoke is already in-flight (e.g. from a previous click),
  // piggy-back on it instead of firing a duplicate.
  if (_inflightSignOut) return _inflightSignOut;

  // The revoke is best-effort and must NEVER throw out of here: this function
  // is documented "do not throw", and a sign-out that rejects is exactly how
  // the session-scoped caches above end up surviving into the next account.
  // `signOut` already wraps its own logout call for the same reason; a
  // SYNCHRONOUS failure (fetch missing/patched, bad URL) skipped that net here.
  try {
    _inflightSignOut = fetch(`${API_CONFIG.BACKEND_URL}/api/auth/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'include',
      body: '{}',
    })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn('[signOutOptimistic] /api/auth/logout failed (cookies will expire via Max-Age):', err.message);
      })
      .finally(() => {
        _inflightSignOut = null;
      });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[signOutOptimistic] /api/auth/logout could not be sent:', err?.message);
    _inflightSignOut = null;
    return Promise.resolve();
  }

  return _inflightSignOut;
};

export const signOut = async () => {
  if (_inflightSignOut) return _inflightSignOut;

  _inflightSignOut = (async () => {
    try {
      // Step 1: server-side revocation + cookie clear (best-effort).
      try {
        await fetch(`${API_CONFIG.BACKEND_URL}/api/auth/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'include',
          // Server only needs the cookies, but include an empty JSON body to keep
          // FastAPI happy and to be consistent with other auth-proxy calls.
          body: '{}',
        });
      } catch (err) {
        // Network blip, backend down, etc. — log and continue with local clears.
        // The cookies will eventually expire on the browser side via Max-Age.
        // Refresh token at Cognito remains valid until natural expiry (30d),
        // which is the same as today's behaviour — no regression.
        // eslint-disable-next-line no-console
        console.warn('[signOut] /api/auth/logout failed (continuing with local clear):', err.message);
      }

      // Steps 2-5: local cleanup (unchanged).
      const { clearAuthToken } = await import('../api/apiClient');
      clearAuthToken();
      try {
        const { clearStoredSession } = await import('../api/sessionApi');
        clearStoredSession();
      } catch { /* non-critical */ }
      try {
        // Reset the resolved chart-type cache so the next account
        // re-resolves its own specialty instead of inheriting this
        // user's value (e.g. a dentist's 'dental' leaking the tooth
        // chart to a non-dentist login). Dynamic import mirrors the
        // cleanup style above and keeps the chart module out of auth.
        const { resetChartTypeCache } = await import('../../charts/core/useChartType');
        resetChartTypeCache();
      } catch { /* non-critical */ }
      try {
        // Reset the other session-scoped caches too (staff roster, tour
        // status, workflow patterns) so they don't leak across accounts.
        (await import('../api/staffApi')).resetStaffRoster();
        (await import('../api/prescriptionTourApi')).resetTourStatusCache();
        (await import('../../components/admission/useAdmissionSearchIndex')).clearAdmissionSearchIndex();
        (await import('../../stores/workflowStore')).default
          .setState({ patterns: null, patternsFetched: false });
      } catch { /* non-critical */ }
      try {
        // Same reason, for the two caches the prescription screen reads: the
        // org's LAB CATALOGUE (Rx dropdown + Tests picker) and the Rx
        // reference blob (the doctor's own top medicines/complaints/diagnoses,
        // rendered as one-tap quick-add pills). Both are org-scoped and both
        // survive a sign-out, which is a router transition and not a reload.
        // Kept in their OWN try so a failure here cannot skip the block above.
        (await import('../../stores/procedureStore')).default
          .setState(PROCEDURE_STORE_SIGNED_OUT);
        (await import('../../components/visit/DigitalPrescriptionForm'))
          .resetRxReferenceCache();
      } catch { /* non-critical */ }
      try { await resetScanShareCaches(); } catch { /* non-critical */ }
      try {
        const { STORAGE_KEYS } = await import('../../constants');
        localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME);
      } catch { /* non-critical */ }
      return amplifySignOut();
    } finally {
      // Always clear the in-flight flag so a future logout (e.g. on the
      // login page after the user logs back in) can fire again.
      _inflightSignOut = null;
    }
  })();

  return _inflightSignOut;
};

/**
 * Check if user is currently authenticated with Cognito.
 * @returns {Promise<boolean>}
 */
export const isAuthenticated = async () => {
  try {
    await getCurrentUser();
    return true;
  } catch {
    return false;
  }
};

/**
 * Wait for Cognito auth session to become available after sign-in.
 * @param {number} delayMs
 * @param {number} maxAttempts
 * @returns {Promise<boolean>}
 */
export const waitForSession = async (delayMs = 250, maxAttempts = 20) => {
  for (let i = 0; i < maxAttempts; i++) {
    try {
      await getCurrentUser();
      return true;
    } catch {
      // not ready yet
    }
    await new Promise((resolve) => setTimeout(resolve, delayMs));
  }
  return false;
};
